import React from 'react'
import { AlertTriangle, ArrowRight, Check, ChevronDown, HelpCircle, Info, Search, ShieldCheck } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import useStore, { PLAN_SLOTS, visiblePlanKeys } from '@/stores/useStore'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, formatCurrency, formatPercent } from '@/lib/utils'
import { focusEmployeeInput, focusEmployeeOutput } from '@/lib/employeeFocus'
import { buildScreenRemediation } from '@/lib/remediation'
import { buildChecksModel } from '@/lib/checksModel'
import { navigateToPlanSection } from '@/lib/planSectionNavigation'
import { AfterTaxAcpRiskCard } from '@/components/SummaryDashboard'
import { planDisplayName } from '@/lib/planDisplayNames'

// Stage ③ Checks: the one place that answers "can I trust this analysis, and is
// this design safe to present". Three tiers in fixed reading order:
//
//   Resolve  – census contradictions, grouped by person, fixed in place.
//   Screens  – the modeled compliance board.
//   Effects  – the plan working as written. Outcomes, never styled as alarms.
//
// Two rules hold the layout together:
//
//   Density. Everything is a one-line row inside a shared frame, and detail is
//   earned by expanding. A census with 200 flagged people must not become 200
//   cards, so nothing here scales by growing a tile.
//
//   Tone is earned by state, not by category. A screen that passes and an
//   exclusion that is working correctly both stay neutral, so the two or three
//   things that want a human read carry the only warm pixels on screen.

const FORMATTERS = {
  currency: formatCurrency,
  percent: formatPercent,
  number: (value) => (value || 0).toLocaleString(),
}
const formatValue = (format, value) => (FORMATTERS[format] || FORMATTERS.number)(value || 0)

// Long lists collapse to this many rows. Past it the page stops growing and
// offers the rest behind one click, so the sections below stay reachable.
const COLLAPSE_AFTER = 8
// Above this, scanning by eye stops working and a filter earns its row.
const FILTER_AFTER = 12

const TONES = {
  good: { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
  warning: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200' },
  danger: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300' },
  neutral: { dot: 'bg-muted-foreground/40', badge: 'bg-muted text-muted-foreground' },
}
const toneOf = (tone) => TONES[tone] || TONES.neutral

// Shared row geometry. Every list on the page uses it, so the three tiers read
// as one board and a row costs the same vertical space wherever it appears.
const ROW = 'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors'
const ROW_BUTTON = cn(ROW, 'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-border')

function Frame({ children, className }) {
  return (
    <div className={cn('divide-y divide-border overflow-hidden rounded-lg border border-border bg-card', className)}>
      {children}
    </div>
  )
}

function SectionHead({ title, count, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      {count !== null && count !== undefined && (
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">{count}</span>
      )}
      <span className="ml-auto flex items-center gap-2">{children}</span>
    </div>
  )
}

function InfoTip({ text, label = 'More detail' }) {
  if (!text) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="shrink-0" aria-label={label}>
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent className="max-w-sm"><p>{text}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function Chevron({ open }) {
  return <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden="true" />
}

// "Showing 8 of 47" plus the toggle. Rendered as a row inside the frame so a
// capped list still looks like one object.
function ShowAllRow({ shown, total, expanded, onToggle }) {
  if (total <= COLLAPSE_AFTER) return null
  return (
    <button type="button" onClick={onToggle} className={cn(ROW_BUTTON, 'justify-center gap-1.5 py-1.5 text-xs font-medium text-muted-foreground')}>
      {expanded ? 'Show fewer' : `Show all ${total}`}
      <span className="font-normal opacity-70">{expanded ? '' : `· ${shown} of ${total} shown`}</span>
    </button>
  )
}

function useCappedList(items, query = '') {
  const [expanded, setExpanded] = React.useState(false)
  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return items
    return items.filter((item) => item.label.toLowerCase().includes(needle))
  }, [items, query])
  const visible = expanded ? filtered : filtered.slice(0, COLLAPSE_AFTER)
  return { visible, filtered, expanded, toggle: () => setExpanded((value) => !value) }
}

// ---------------------------------------------------------------------------
// Tier 1: resolve

function ConflictRow({ conflict, onApply, onJumpToInput, onJumpToOutput }) {
  const [open, setOpen] = React.useState(false)
  const extra = conflict.notes.length - 1

  return (
    <div>
      <div className={cn(ROW, 'gap-2 hover:bg-muted/40')}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border"
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
          <span className="w-36 shrink-0 truncate font-medium text-foreground sm:w-44">{conflict.label}</span>
          <span className="hidden min-w-0 flex-1 truncate text-xs text-muted-foreground sm:block">{conflict.notes[0]}</span>
          {extra > 0 && <span className="shrink-0 rounded bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">+{extra}</span>}
          <Chevron open={open} />
        </button>

        {/* The primary fix stays on the row: the common case is one click and
            never opening the detail at all. */}
        <span className="flex shrink-0 items-center gap-1">
          {conflict.fixes.slice(0, 1).map((fix) => (
            <button
              key={`${fix.kind}:${fix.field}`}
              type="button"
              onClick={() => (fix.kind === 'set' ? onApply(conflict, fix) : onJumpToInput(conflict, fix.field))}
              title={fix.detail || undefined}
              className="cursor-pointer whitespace-nowrap rounded border border-amber-400 bg-amber-100/70 px-2 py-0.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-200/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500 dark:border-amber-500/50 dark:bg-amber-500/15 dark:text-amber-200"
            >
              {fix.label}
            </button>
          ))}
        </span>
      </div>

      {open && (
        <div className="space-y-2 bg-muted/30 px-3 py-2.5 pl-8">
          <ul className="space-y-1">
            {conflict.notes.map((note) => (
              <li key={note} className="text-xs leading-relaxed text-foreground/80 [overflow-wrap:anywhere]">{note}</li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-1.5">
            {conflict.fixes.map((fix) => (
              <button
                key={`${fix.kind}:${fix.field}`}
                type="button"
                onClick={() => (fix.kind === 'set' ? onApply(conflict, fix) : onJumpToInput(conflict, fix.field))}
                title={fix.detail || undefined}
                className="cursor-pointer rounded border border-border bg-background px-2 py-0.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border"
              >
                {fix.label}
              </button>
            ))}
            <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
              {conflict.compensation ? <span className="tabular-nums">{formatCurrency(conflict.compensation)}</span> : null}
              <button
                type="button"
                onClick={() => onJumpToOutput(conflict)}
                className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-foreground"
              >
                Results
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tier 2: screens

function ScreenRow({ entry, metrics }) {
  const [open, setOpen] = React.useState(false)
  const tone = toneOf(entry.screen?.badge?.tone || 'neutral')
  const remediation = entry.remediationId ? buildScreenRemediation(entry.remediationId, metrics) : null

  return (
    <div>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} className={ROW_BUTTON}>
        <span className={cn('h-2 w-2 shrink-0 rounded-full', tone.dot)} aria-hidden="true" />
        <span className="w-40 shrink-0 truncate font-medium text-foreground sm:w-52">{entry.label}</span>
        <span className="w-20 shrink-0 text-right font-semibold tabular-nums text-foreground">
          {entry.available ? formatValue(entry.format, metrics?.[entry.field]) : 'N/A'}
        </span>
        {entry.screen?.badge && (
          <span className={cn('hidden shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold sm:inline-flex', tone.badge)}>
            {entry.screen.badge.text}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <Chevron open={open} />
        </span>
      </button>

      {open && (
        <div className="space-y-2 bg-muted/30 px-3 py-2.5 pl-8">
          {entry.screen?.badge && (
            <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold sm:hidden', tone.badge)}>
              {entry.screen.badge.text}
            </span>
          )}
          <p className="text-xs leading-relaxed text-foreground/80">{entry.screen?.title}</p>
          {remediation && (
            <div className="space-y-1 border-l-2 border-border pl-2.5 text-xs leading-relaxed text-muted-foreground">
              <p><span className="font-medium text-foreground">Why: </span>{remediation.why}</p>
              <p><span className="font-medium text-foreground">How to address: </span>{remediation.fix}</p>
            </div>
          )}
          <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
            <InfoTip text={entry.tooltip} label={`What ${entry.label} measures`} />
            <span>Modeled screen, not a formal test result.</span>
          </p>
        </div>
      )}
    </div>
  )
}

function ComparisonScreens({ entries }) {
  const rows = [...new Set(entries.flatMap((entry) => entry.model.screens.map((screen) => screen.id)))]
    .map((id) => ({
      id,
      label: entries.flatMap((entry) => entry.model.screens).find((screen) => screen.id === id)?.label || id,
    }))
  const template = `minmax(9rem, .8fr) repeat(${entries.length}, minmax(11rem, 1fr))`

  return (
    <div role="region" aria-label="Plan compliance comparison" className="overflow-x-auto border border-border bg-card">
      <div className="grid min-w-max bg-muted/40 text-xs" style={{ gridTemplateColumns: template }}>
        <div className="border-b border-border px-3 py-2 font-medium text-muted-foreground">Screen</div>
        {entries.map((entry) => (
          <div key={entry.key} className={cn('border-b border-l border-border px-3 py-2 text-left font-semibold', entry.key === 'planA' && 'bg-primary/5')}>
            {entry.displayName}
            {entry.key === 'planA' && <span className="ml-1.5 text-[10px] font-normal text-primary">Starting point</span>}
          </div>
        ))}

        {rows.flatMap((row) => {
          const cells = [
            <div key={`${row.id}-label`} className="border-b border-border px-3 py-2.5 font-medium">{row.label}</div>,
          ]
          entries.forEach((entry) => {
            const screen = entry.model.screens.find((candidate) => candidate.id === row.id)
            const tone = toneOf(screen?.screen?.badge?.tone || 'neutral')
            cells.push(
              <div
                key={`${row.id}-${entry.key}`}
                title={screen?.screen?.title || 'Not applicable to this plan'}
                className={cn('min-w-0 border-b border-l border-border px-3 py-2 text-left', entry.key === 'planA' && 'bg-primary/[0.035]')}
              >
                <span className="block truncate font-semibold tabular-nums">
                  {screen?.available ? formatValue(screen.format, entry.analysis?.metrics?.[screen.field]) : 'N/A'}
                </span>
                <span className={cn('mt-1 inline-flex max-w-full truncate rounded-full px-2 py-0.5 text-[10px] font-semibold', tone.badge)}>
                  {screen?.screen?.badge?.text || 'Not applicable'}
                </span>
              </div>,
            )
          })
          return cells
        })}
      </div>
    </div>
  )
}

function ComparisonEffects({ entries }) {
  const rows = [
    {
      label: 'Limited by eligibility rules',
      value: (entry) => entry.model.eligibility.limited.length,
      detail: (entry) => entry.model.eligibility.totalCount ? `of ${entry.model.eligibility.totalCount}` : '',
    },
    {
      label: 'Handled automatically',
      value: (entry) => entry.model.counts.advisories,
      detail: () => '',
    },
    {
      label: 'Plan-setting notes',
      value: (entry) => entry.model.counts.planNotes,
      detail: (entry) => entry.model.planNotes.join(' '),
    },
    {
      label: 'Unallocated profit-sharing pools',
      value: (entry) => entry.model.allocation.unallocated.length,
      detail: (entry) => entry.model.allocation.unallocated.map((component) => (
        `${formatCurrency(component.unallocatedResidual)} of ${formatCurrency(component.requestedPool)} unallocated`
      )).join(' · '),
    },
  ]
  const template = `minmax(13rem, .9fr) repeat(${entries.length}, minmax(11rem, 1fr))`

  return (
    <div role="region" aria-label="Plan effects comparison" className="overflow-x-auto border border-border bg-card">
      <div className="grid min-w-max text-xs" style={{ gridTemplateColumns: template }}>
        <div className="border-b border-border bg-muted/40 px-3 py-2 font-medium text-muted-foreground">Effect</div>
        {entries.map((entry) => (
          <div key={entry.key} className={cn('border-b border-l border-border bg-muted/40 px-3 py-2 font-semibold', entry.key === 'planA' && 'bg-primary/5')}>
            {entry.displayName}{entry.key === 'planA' && <span className="ml-1.5 text-[10px] font-normal text-primary">Starting point</span>}
          </div>
        ))}
        {rows.flatMap((row) => [
          <div key={`${row.label}-label`} className="border-b border-border px-3 py-2.5 font-medium">{row.label}</div>,
          ...entries.map((entry) => {
            const value = row.value(entry)
            const detail = row.detail(entry)
            return (
              <div key={`${row.label}-${entry.key}`} className={cn('border-b border-l border-border px-3 py-2.5', entry.key === 'planA' && 'bg-primary/[0.035]')} title={detail || undefined}>
                <span className="block font-semibold tabular-nums">{value}</span>
                {detail && <span className="mt-0.5 block max-w-64 truncate text-[10px] text-muted-foreground">{detail}</span>}
              </div>
            )
          }),
        ])}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tier 3: effects. One frame, one row per kind, detail on expand. Eligibility
// exclusions are reported as a distribution over the rule that stopped people,
// which is both denser and more useful than a sentence per person.

function EffectRow({ label, summary, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} className={ROW_BUTTON}>
        <span className="min-w-0 flex-1 truncate text-foreground">{label}</span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{summary}</span>
        <Chevron open={open} />
      </button>
      {open && <div className="bg-muted/30 px-3 py-2.5">{children}</div>}
    </div>
  )
}

function EligibilityDetail({ eligibility, onJumpToInput }) {
  const [openRule, setOpenRule] = React.useState(null)
  const total = eligibility.limited.length

  return (
    <div className="space-y-2.5">
      <div className="space-y-1">
        {eligibility.groups.map((group) => {
          const open = openRule === group.rule
          const share = total > 0 ? (group.people.length / total) * 100 : 0
          return (
            <div key={group.rule}>
              <button
                type="button"
                onClick={() => setOpenRule(open ? null : group.rule)}
                aria-expanded={open}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded px-1 py-1 text-left transition-colors hover:bg-muted/60"
              >
                <span className="w-32 shrink-0 truncate text-xs text-foreground sm:w-40">{group.label}</span>
                <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full rounded-full bg-foreground/25" style={{ width: `${share}%` }} />
                </span>
                <span className="w-8 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{group.people.length}</span>
                <Chevron open={open} />
              </button>
              {open && (
                <ul className="ml-2 space-y-0.5 border-l border-border py-1 pl-2.5">
                  {group.people.map((person) => (
                    <li key={person.id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <button
                        type="button"
                        onClick={() => onJumpToInput(person)}
                        className="cursor-pointer font-medium text-foreground underline decoration-dotted underline-offset-2 hover:decoration-solid"
                      >
                        {person.label}
                      </button>
                      <span className="text-muted-foreground">{person.details}</span>
                      {person.isLtpt && <span className="rounded bg-muted px-1 text-[10px] font-semibold text-muted-foreground">LTPT</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Failing an age, service, or hours requirement means no deferrals and no employer money. Long-term part-time
        employees may still defer under SECURE 2.0 but receive no employer contributions. Someone failing two rules is
        counted under both, so the rows sum to more than {total}.
        {eligibility.ltptCount > 0 && ` ${eligibility.ltptCount} modeled as LTPT.`}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------

export default function ChecksStage() {
  const employees = useStore((state) => state.employees)
  const updateEmployee = useStore((state) => state.updateEmployee)
  const setWorkspaceStage = useStore((state) => state.setWorkspaceStage)
  const visibleKeys = useStore((state) => visiblePlanKeys(state).join('|')).split('|').filter(Boolean)
  const comparing = visibleKeys.length > 1
  const displayPlanKey = 'planA'
  const plan = useStore((state) => state[displayPlanKey])
  const analysis = useStore((state) => state.analysis[displayPlanKey])
  const comparisonSnapshot = useStore(useShallow((state) => [
    state.planA, state.planB, state.planC, state.planD,
    state.analysis.planA, state.analysis.planB, state.analysis.planC, state.analysis.planD,
  ]))
  const comparisonEntries = React.useMemo(() => PLAN_SLOTS.flatMap((key, index) => {
    const entryPlan = comparisonSnapshot[index]
    const entryAnalysis = comparisonSnapshot[index + 4]
    return entryPlan && visibleKeys.includes(key) ? [{
      key,
      plan: entryPlan,
      displayName: planDisplayName(key, entryPlan.name),
      analysis: entryAnalysis,
      model: buildChecksModel({ plan: entryPlan, analysis: entryAnalysis, employees }),
    }] : []
  }), [comparisonSnapshot, employees, visibleKeys])

  const model = React.useMemo(
    () => buildChecksModel({ plan, analysis, employees }),
    [plan, analysis, employees],
  )
  const { counts } = model

  const [query, setQuery] = React.useState('')
  const conflicts = useCappedList(model.conflicts, query)
  const advisories = useCappedList(model.advisories)

  // The census table only exists on the People stage, so a jump has to switch
  // stages first and let it mount before the focus event fires. (The old
  // banners dispatched straight into an unmounted listener and did nothing.)
  const jumpToInput = React.useCallback((person, field = 'id') => {
    if (!person || person.employeeIndex < 0) return
    setWorkspaceStage('people')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusEmployeeInput({ employeeIndex: person.employeeIndex, field }))
    })
  }, [setWorkspaceStage])

  const jumpToOutput = React.useCallback((person) => {
    setWorkspaceStage('results')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusEmployeeOutput({ employeeId: person.id }))
    })
  }, [setWorkspaceStage])

  const applyFix = React.useCallback((person, fix) => {
    updateEmployee(person.id, { [fix.field]: fix.value })
  }, [updateEmployee])

  const openPlanEligibility = React.useCallback(() => {
    setWorkspaceStage('plan')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        navigateToPlanSection('eligibility')
        document.querySelector(`#${displayPlanKey}-plan-eligibility-rules, #plan-eligibility-rules`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }, [displayPlanKey, setWorkspaceStage])

  const plansNeedingAttention = comparing
    ? comparisonEntries.filter((entry) => entry.model.verdict !== 'clear').length
    : (model.verdict !== 'clear' ? 1 : 0)
  const attention = plansNeedingAttention > 0
  const headline = comparing
    ? plansNeedingAttention > 0
      ? `${plansNeedingAttention} of ${comparisonEntries.length} options need review`
      : 'All shown options are ready'
    : model.verdict === 'conflicts'
    ? `${counts.conflicts} record${counts.conflicts === 1 ? '' : 's'} to resolve`
    : model.verdict === 'attention'
      ? `${counts.screensNeedingAttention} plan rule${counts.screensNeedingAttention === 1 ? '' : 's'} need review`
      : 'No plan issues found'
  const hasEffects = model.eligibility.limited.length > 0
    || counts.planNotes > 0
    || counts.advisories > 0
    || model.allocation.unallocated.length > 0

  return (
    <div className={cn('mx-auto w-full space-y-5 pb-6 pt-4', comparing ? 'max-w-[1600px]' : 'max-w-4xl')}>
      <div role="note" className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-0.5">
          <p className="font-medium text-foreground">Before You Continue</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            These are planning checks, not final compliance results. Ask the plan administrator to confirm them using
            the final plan document and complete employee data.
          </p>
        </div>
      </div>

      {/* Verdict: one line. It used to be a padded box with two paragraphs,
          which is a lot of page to spend restating the counts below it. */}
      <div className={cn(
        'flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg border px-3 py-2 text-sm',
        attention
          ? 'border-amber-300 bg-amber-50/70 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
          : 'border-emerald-300 bg-emerald-50/70 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200',
      )}>
        {attention
          ? <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          : <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="font-semibold">{headline}</span>
        {!comparing && <span className="ml-auto text-xs tabular-nums opacity-80">
          {counts.eligibilityLimited > 0 && `${counts.eligibilityLimited} limited by eligibility`}
          {counts.eligibilityLimited > 0 && counts.advisories > 0 && ' · '}
          {counts.advisories > 0 && `${counts.advisories} handled automatically`}
        </span>}
      </div>

      {/* ---- Tier 1: resolve ------------------------------------------- */}
      <section id="checks-resolve" className="scroll-mt-16 space-y-2">
        <SectionHead title="Fix Employee Details" count={counts.conflicts}>
          {model.conflicts.length > FILTER_AFTER && (
            <span className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter by name"
                aria-label="Filter records to resolve"
                className="h-7 w-40 rounded-md border border-border bg-background pl-7 pr-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border"
              />
            </span>
          )}
        </SectionHead>

        {counts.conflicts === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            No conflicting employee details found.
          </div>
        ) : (
          <Frame>
            {conflicts.visible.map((conflict) => (
              <ConflictRow
                key={conflict.id}
                conflict={conflict}
                onApply={applyFix}
                onJumpToInput={jumpToInput}
                onJumpToOutput={jumpToOutput}
              />
            ))}
            {conflicts.filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground">No record matches &ldquo;{query}&rdquo;.</p>
            )}
            <ShowAllRow
              shown={conflicts.visible.length}
              total={conflicts.filtered.length}
              expanded={conflicts.expanded}
              onToggle={conflicts.toggle}
            />
          </Frame>
        )}
      </section>

      {/* ---- Tier 2: screens -------------------------------------------- */}
      <section id="checks-screens" className="scroll-mt-16 space-y-2">
        <SectionHead title="Review Plan Rules" count={model.screens.length} />
        <p className="text-xs text-muted-foreground">Modeled deferral and contribution tests by employee classification.</p>
        <details className="group rounded-lg border border-border bg-card">
          <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm font-medium marker:content-none">
            Show technical testing details
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="space-y-2 border-t border-border p-2">
            {comparing ? (
              <ComparisonScreens entries={comparisonEntries} />
            ) : (
              <Frame>
                {model.screens.map((entry) => (
                  <ScreenRow key={entry.id} entry={entry} metrics={analysis?.metrics} />
                ))}
              </Frame>
            )}
            {comparing ? (
              <div className="grid gap-2 xl:grid-cols-2">
                {comparisonEntries.map((entry) => (
                  <AfterTaxAcpRiskCard key={entry.key} data={entry.analysis?.metrics?.megaBackdoorAcp} planName={entry.displayName} />
                ))}
              </div>
            ) : <AfterTaxAcpRiskCard data={analysis?.metrics?.megaBackdoorAcp} />}
          </div>
        </details>
      </section>

      {/* ---- Tier 3: effects -------------------------------------------- */}
      <section id="checks-effects" className="scroll-mt-16 space-y-2">
        <SectionHead title="Other Effects" count={null} />
        {comparing ? (
          <ComparisonEffects entries={comparisonEntries} />
        ) : hasEffects ? (
          <Frame>
            {model.eligibility.limited.length > 0 && (
              <EffectRow
                label="Limited by eligibility rules"
                summary={model.eligibility.totalCount
                  ? `${model.eligibility.limited.length} of ${model.eligibility.totalCount}`
                  : String(model.eligibility.limited.length)}
              >
                <EligibilityDetail eligibility={model.eligibility} onJumpToInput={jumpToInput} />
                <button
                  type="button"
                  onClick={openPlanEligibility}
                  className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  Edit the eligibility rules
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
              </EffectRow>
            )}

            {counts.advisories > 0 && (
              <EffectRow label="Handled automatically" summary={String(counts.advisories)}>
                <ul className="space-y-1">
                  {advisories.visible.map((person) => (
                    <li key={person.id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <button
                        type="button"
                        onClick={() => jumpToInput(person)}
                        className="cursor-pointer font-medium text-foreground underline decoration-dotted underline-offset-2 hover:decoration-solid"
                      >
                        {person.label}
                      </button>
                      <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">{person.notes.join(' ')}</span>
                    </li>
                  ))}
                </ul>
                {model.advisories.length > COLLAPSE_AFTER && (
                  <button
                    type="button"
                    onClick={advisories.toggle}
                    className="mt-1.5 cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    {advisories.expanded ? 'Show fewer' : `Show all ${model.advisories.length}`}
                  </button>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">Valid input the model resolved under the plan&rsquo;s own rules. No action needed.</p>
              </EffectRow>
            )}

            {counts.planNotes > 0 && (
              <EffectRow label="Plan settings affecting employee results" summary={String(counts.planNotes)}>
                <ul className="space-y-1">
                  {model.planNotes.map((note) => (
                    <li key={note} className="text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">{note}</li>
                  ))}
                </ul>
              </EffectRow>
            )}

            {/* Only when a pool did not fully allocate. A fully-allocated
                breakdown repeats what the results already show. */}
            {model.allocation.unallocated.length > 0 && (
              <EffectRow
                label="Profit-sharing pools that did not fully allocate"
                summary={String(model.allocation.unallocated.length)}
                defaultOpen
              >
                <ul className="space-y-1">
                  {model.allocation.unallocated.map((component) => (
                    <li key={component.componentId} className="text-xs leading-relaxed text-foreground/80">
                      <span className="font-medium capitalize">{String(component.componentType).replaceAll('_', ' ')}</span>
                      {': '}
                      {formatCurrency(component.unallocatedResidual)} of the {formatCurrency(component.requestedPool)} requested
                      pool had nowhere to go, so {formatCurrency(component.allocatedPool)} was allocated.
                      {component.assumptionSet ? ` ${component.assumptionSet.label}.` : ''}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Usually a formula that caps out before the pool is exhausted, or an allocation group with nobody in it.
                </p>
              </EffectRow>
            )}
          </Frame>
        ) : (
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            {model.eligibility.enabled
              ? 'Everyone on the employee list participates, and nothing was capped or reallocated.'
              : 'Eligibility rules are off, so everyone participates. Nothing was capped or reallocated.'}
          </div>
        )}
      </section>

    </div>
  )
}
